// @ts-check
// Autoguardado local (SOLO `localStorage`, sin red) del estado completo de un
// editor de texto basado en `state/algorithm-model.js`: todos los algoritmos
// (`{ id, nombre, texto }`) y cuál está activo.
//
// ## Por qué existe este archivo
//
// `features/pseudocode-editor/pseudocode-persistence.js` (TSK-0094) y
// `features/python-editor/python-persistence.js` (TSK-0100) eran ~82 %
// idénticos: `VERSION_ESQUEMA`, `LIMITE_BYTES`, `obtenerStorage`,
// `esErrorDeCuota`, `tieneFormaDeEnvoltorio`, `esEstadoValido`, el
// round-trip completo y el debounce de 800 ms, todo repetido. Solo cambiaban
// el nombre de las funciones y la clave de almacenamiento. Mismo molde que
// `state/attempt-persistence.js`: aquí la lógica, en cada feature un adaptador
// que fija su clave y conserva su API pública.
//
// ## LÍMITE DURO: el formato guardado no puede cambiar ni un byte
//
// Estos datos ya están en los navegadores de los alumnos. Cambiar la clave, el
// nombre de un campo del envoltorio o `VERSION_ESQUEMA` invalidaría (y
// borraría, porque leer algo incompatible LIMPIA la clave) el trabajo que
// tengan sin guardar. Por eso:
//
//   - la clave de almacenamiento la sigue declarando cada feature, como
//     LITERAL, en su propio archivo. No se compone aquí a partir del nombre
//     del editor: un literal es lo que un `grep` encuentra y lo que
//     `tests/unit/state/editor-drafts-namespaces.spec.js` puede clasificar;
//   - el envoltorio conserva exactamente los mismos cuatro campos y el mismo
//     orden (`version`, `guardadoEn`, `algoritmos`, `activoId`);
//   - la clave de sonda de escritura y el nombre que aparece en los mensajes
//     de `TypeError` se reciben como parámetro, para que ni siquiera esos
//     detalles observables cambien.
//
// ## Qué NO hace este módulo
//
// No es la fuente de verdad (eso es el backend), no importa
// `services/api-client.js` ni ningún `*-api.js`, no hace ninguna llamada
// `fetch`/HTTP y no toca el DOM (docs/03-frontend.md §4.3). Tampoco decide
// CUÁNDO ha cambiado el estado: el llamador invoca `notificarCambio()`.
//
// ## Relación con `state/editor-drafts.js`
//
// `editor-drafts.js` decide qué claves de `localStorage` se mueven a un
// respaldo por usuario al entrar/salir de sesión, y lo hace con una lista de
// prefijos escrita a mano. Este módulo NO pasa a ser el dueño de esos
// namespaces, a propósito: el dueño tendría que ser `editor-drafts.js`, y para
// que las features tomaran de allí su prefijo habría que tocar también los dos
// archivos de `features/diagram-editor/` (ZONA PROTEGIDA) o dejar la fuente de
// verdad partida en dos, que es peor que la duplicación actual. Con las claves
// como literales en cada feature, el test de namespaces sigue dando la misma
// garantía sin tocar nada de eso.

import { esAlgoritmo, ID_PRINCIPAL } from './algorithm-model.js';
// El borrador ya no es uno por editor, sino uno por ejercicio: la composición
// de la clave se comparte para que las tres features no puedan separarse en
// silencio (ver `state/ambito-borrador.js`).
import { claveConAmbito } from './ambito-borrador.js';

/** @typedef {import('./algorithm-model.js').Algoritmo} Algoritmo */

/**
 * Versión del esquema del envoltorio guardado en `localStorage`. Si al leer
 * la versión almacenada no coincide, los datos se consideran incompatibles y
 * se descartan (sin intento de migración): no hay versiones previas de las que
 * migrar. Mismo criterio que `diagram-editor/editor-persistence.js`.
 */
export const VERSION_ESQUEMA = 1;

/**
 * Límite de tamaño (bytes, UTF-8) del envoltorio serializado. Mismo criterio y
 * mismo valor que `diagram-editor/editor-persistence.js#LIMITE_BYTES`: deja
 * margen para otras claves de la aplicación y evita dejar el storage al borde
 * de la cuota con un solo editor. Si el estado serializado supera este límite,
 * NO se escribe (degradación explícita) en lugar de arriesgarse a un fallo de
 * cuota.
 */
export const LIMITE_BYTES = 4 * 1024 * 1024;

/**
 * @typedef {{ algoritmos: Algoritmo[], activoId: string }} EstadoGuardable
 */

/**
 * @typedef {(
 *   | { ok: true }
 *   | { ok: false, motivo: 'no_disponible' | 'excede_limite' | 'cuota_excedida' | 'error_desconocido' }
 * )} ResultadoGuardado
 */

/**
 * Devuelve la referencia a `localStorage` si está disponible y utilizable
 * (algunos navegadores en modo privado la exponen pero lanzan en el primer
 * `setItem`; otros lanzan al simplemente acceder a la propiedad). Devuelve
 * `null` en cualquier caso de fallo, sin propagar la excepción.
 * @returns {Storage | null}
 */
function obtenerStorage() {
  try {
    const storage = globalThis.localStorage;
    if (!storage) return null;
    return storage;
  } catch {
    return null;
  }
}

/**
 * @param {unknown} error
 * @returns {boolean}
 */
function esErrorDeCuota(error) {
  if (typeof globalThis.DOMException !== 'undefined' && error instanceof globalThis.DOMException) {
    return (
      error.name === 'QuotaExceededError' ||
      error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
      error.code === 22 ||
      error.code === 1014
    );
  }
  // Algunos entornos (p.ej. jsdom, motores antiguos) lanzan un Error simple
  // con un mensaje reconocible en lugar de un DOMException tipado.
  return error instanceof Error && /quota/i.test(error.message);
}

/**
 * @param {unknown} valor
 * @returns {valor is { version: number, algoritmos: unknown[], activoId: string }}
 */
function tieneFormaDeEnvoltorio(valor) {
  if (typeof valor !== 'object' || valor === null) return false;
  const objeto = /** @type {Record<string, unknown>} */ (valor);
  return (
    typeof objeto.version === 'number' &&
    Array.isArray(objeto.algoritmos) &&
    typeof objeto.activoId === 'string'
  );
}

/**
 * Valida que el envoltorio completo tenga la forma esperada de un estado de
 * editor guardable: cada elemento de `algoritmos` es un `Algoritmo` válido,
 * existe el algoritmo principal fijo (`ID_PRINCIPAL`, invariante de
 * `algorithm-model.js`) y `activoId` referencia a alguno de los algoritmos
 * presentes (nunca un id huérfano).
 * @param {{ version: number, algoritmos: unknown[], activoId: string }} envoltorio
 * @returns {envoltorio is { version: number, algoritmos: Algoritmo[], activoId: string }}
 */
function esEstadoValido(envoltorio) {
  const { algoritmos, activoId } = envoltorio;
  if (!algoritmos.every(esAlgoritmo)) return false;
  const listaAlgoritmos = /** @type {Algoritmo[]} */ (algoritmos);
  if (!listaAlgoritmos.some((algoritmo) => algoritmo.id === ID_PRINCIPAL)) return false;
  if (!listaAlgoritmos.some((algoritmo) => algoritmo.id === activoId)) return false;
  return true;
}

/**
 * Crea el almacén de autoguardado de un editor concreto.
 *
 * @param {{
 *   claveAlmacenamiento: string,
 *   claveSonda: string,
 *   nombreFuncionGuardar: string,
 * }} opciones
 *   `claveAlmacenamiento`: literal exacto de la clave de `localStorage` (ver
 *   "LÍMITE DURO" arriba). `claveSonda`: clave efímera de la sonda de
 *   escritura, escrita y borrada al instante. `nombreFuncionGuardar`: nombre
 *   con el que la feature expone `guardarEstado`, usado SOLO para que los
 *   mensajes de `TypeError` sigan nombrando la función que el llamador ve.
 * @returns {{
 *   esAlmacenamientoDisponible: () => boolean,
 *   guardarEstado: (algoritmos: Algoritmo[], activoId: string, ambito?: string) => ResultadoGuardado,
 *   cargarEstado: (ambito?: string) => EstadoGuardable | null,
 *   limpiarEstado: (ambito?: string) => void,
 *   crearAutoguardado: (
 *     obtenerEstadoActual: () => EstadoGuardable,
 *     opciones?: { retrasoMs?: number, ambito?: string },
 *   ) => { notificarCambio: () => void, detener: () => void },
 * }}
 */
export function crearPersistenciaAlgoritmos({
  claveAlmacenamiento,
  claveSonda,
  nombreFuncionGuardar,
}) {
  /**
   * Indica si el almacenamiento local está disponible y aceptando escrituras
   * en este momento (sonda de escritura de un byte, revertida al instante).
   * Útil para que la capa de UI decida si avisar de que el autoguardado está
   * desactivado, sin necesidad de intentar guardar el estado real.
   * @returns {boolean}
   */
  function esAlmacenamientoDisponible() {
    const storage = obtenerStorage();
    if (!storage) return false;
    try {
      storage.setItem(claveSonda, '1');
      storage.removeItem(claveSonda);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Limpieza explícita del autoguardado local. Se usa tanto al descartar datos
   * inválidos como cuando un consumidor quiere olvidar el borrador local. No
   * lanza si el almacenamiento no está disponible.
   * @param {string} [ambito] Ejercicio cuyo borrador se limpia. Sin él, el
   *   borrador libre (ver `state/ambito-borrador.js`).
   * @returns {void}
   */
  function limpiarEstado(ambito) {
    const storage = obtenerStorage();
    if (!storage) return;
    try {
      storage.removeItem(claveConAmbito(claveAlmacenamiento, ambito));
    } catch {
      // No hay nada razonable que hacer si ni siquiera `removeItem` funciona;
      // se ignora para no romper el flujo del editor.
    }
  }

  /**
   * Serializa y guarda el estado completo (todos los algoritmos + cuál está
   * activo) en `localStorage`, sustituyendo cualquier guardado previo.
   *
   * Degrada explícitamente en lugar de lanzar cuando el almacenamiento no está
   * disponible, está deshabilitado (modo privado), se excede la cuota, o el
   * estado supera `LIMITE_BYTES`: en todos esos casos el editor debe poder
   * seguir funcionando en memoria sin autoguardado.
   *
   * @param {Algoritmo[]} algoritmos
   * @param {string} activoId
   * @param {string} [ambito] Ejercicio al que pertenece este borrador. Sin
   *   él, el borrador libre (ver `state/ambito-borrador.js`).
   * @returns {ResultadoGuardado}
   */
  function guardarEstado(algoritmos, activoId, ambito) {
    const storage = obtenerStorage();
    if (!storage) {
      return { ok: false, motivo: 'no_disponible' };
    }

    // Un `algoritmos`/`activoId` con forma inválida es un error de contrato
    // del llamador (no un problema de almacenamiento): se deja propagar.
    if (!Array.isArray(algoritmos) || !algoritmos.every(esAlgoritmo)) {
      throw new TypeError(`${nombreFuncionGuardar} requiere un array de Algoritmo válidos`);
    }
    if (typeof activoId !== 'string' || activoId.length === 0) {
      throw new TypeError(`${nombreFuncionGuardar} requiere un activoId no vacío`);
    }

    const envoltorio = {
      version: VERSION_ESQUEMA,
      guardadoEn: new Date().toISOString(),
      algoritmos,
      activoId,
    };

    let serializado;
    try {
      serializado = JSON.stringify(envoltorio);
    } catch {
      return { ok: false, motivo: 'error_desconocido' };
    }

    const tamanoBytes = new globalThis.TextEncoder().encode(serializado).length;
    if (tamanoBytes > LIMITE_BYTES) {
      return { ok: false, motivo: 'excede_limite' };
    }

    try {
      storage.setItem(claveConAmbito(claveAlmacenamiento, ambito), serializado);
      return { ok: true };
    } catch (error) {
      if (esErrorDeCuota(error)) {
        return { ok: false, motivo: 'cuota_excedida' };
      }
      return { ok: false, motivo: 'error_desconocido' };
    }
  }

  /**
   * Lee y valida el estado guardado en `localStorage`.
   *
   * Si el almacenamiento no está disponible, no hay nada guardado, el JSON
   * está corrupto, la versión del esquema no coincide, o el contenido no
   * corresponde a una lista de `Algoritmo` válida (incluyendo la invariante
   * "existe el algoritmo principal" y "`activoId` referencia a un algoritmo
   * presente"), se descarta el dato (limpiando la clave para no dejar basura
   * ni reintentar sobre el mismo dato corrupto) y se devuelve `null` sin
   * romper el editor.
   *
   * @param {string} [ambito] Ejercicio cuyo borrador se lee. Sin él, el
   *   borrador libre (ver `state/ambito-borrador.js`).
   * @returns {EstadoGuardable | null}
   */
  function cargarEstado(ambito) {
    const storage = obtenerStorage();
    if (!storage) return null;

    let bruto;
    try {
      bruto = storage.getItem(claveConAmbito(claveAlmacenamiento, ambito));
    } catch {
      return null;
    }
    if (bruto === null) return null;

    /** @type {unknown} */
    let envoltorio;
    try {
      envoltorio = JSON.parse(bruto);
    } catch {
      limpiarEstado();
      return null;
    }

    if (!tieneFormaDeEnvoltorio(envoltorio) || envoltorio.version !== VERSION_ESQUEMA) {
      limpiarEstado();
      return null;
    }

    if (!esEstadoValido(envoltorio)) {
      limpiarEstado();
      return null;
    }

    return { algoritmos: envoltorio.algoritmos, activoId: envoltorio.activoId };
  }

  /**
   * Crea un programador de autoguardado con "debounce": agrupa cambios
   * rápidos y consecutivos (p.ej. cada pulsación de tecla en cualquier
   * editor) en un único guardado tras `retrasoMs` de inactividad, en vez de
   * guardar en cada pulsación.
   *
   * @param {() => EstadoGuardable} obtenerEstadoActual
   * @param {{ retrasoMs?: number, ambito?: string }} [opciones]
   * @returns {{ notificarCambio: () => void, detener: () => void }}
   */
  function crearAutoguardado(obtenerEstadoActual, opciones = {}) {
    const retrasoMs = opciones.retrasoMs ?? 800;
    const ambito = opciones.ambito;
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let temporizador;

    function notificarCambio() {
      if (temporizador !== undefined) {
        clearTimeout(temporizador);
      }
      temporizador = setTimeout(() => {
        temporizador = undefined;
        const { algoritmos, activoId } = obtenerEstadoActual();
        guardarEstado(algoritmos, activoId, ambito);
      }, retrasoMs);
    }

    function detener() {
      if (temporizador !== undefined) {
        clearTimeout(temporizador);
        temporizador = undefined;
      }
    }

    return { notificarCambio, detener };
  }

  return {
    esAlmacenamientoDisponible,
    guardarEstado,
    cargarEstado,
    limpiarEstado,
    crearAutoguardado,
  };
}
